import { getInstrumentsFromMedia } from '@arpeggi/shared'
import { observer } from 'mobx-react-lite'
import { ComponentProps, FC, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ArpRegistry from '../../../../../features/arp-registry/ArpRegistry'
import { styled } from '../../../../lib/stitches'
import {
  getSampleOpenseaLink,
  getSongOpenseaLink,
  getStemOpenseaLink
} from '../../../../utils/media'
import Button from '../../../core/Button/Button'
import { Box } from '../../../core/Components'
import { OpenseaLogo } from '../../../core/icons'
import { MediaTypes } from '../../../core/Utils'
import TweetButton from '../../../detail-pages/shared/TweetButton'
import { gray1 } from '../../../platform/color'
import { SamplePlayer, StemPlayer, TokenSongPlayer } from '../../../player/Player'
import CurrentlyPlayingInfo from '../../../profile/media-content/ProfileCurrentlyPlaying'
import BlankState from './BlankState'
import Contributors from './Contributors'
import {
  normalizeSubtypes,
  SEGMENT_PREVIEW_LOCATIONS,
  Spinner,
  useAsyncEffect,
  useRemix
} from './utils'

const StyledPreviewBar = styled('div', {
  flex: 1,
  minWidth: '400px',
  maxWidth: '500px',
  maxHeight: 'calc(100vh - 83px)',
  position: 'sticky',
  top: '67px'
})

const StyledPreviewContainerWrapper = styled('div', {
  width: '100%',
  background: gray1,
  overflow: 'hidden',
  boxShadow: '0px 4px 16px 0px #00000026',
  borderRadius: '16px',
  boxSizing: 'content-box',
  position: 'relative',
  '&::after': {
    content: '',
    display: 'block',
    paddingBottom: '100%'
  }
})

const StyledPreviewContainer = styled('div', {
  width: '100%',
  height: '100%',
  position: 'absolute'
})

export const PreviewBar = StyledPreviewBar
export const PreviewContainerWrapper = StyledPreviewContainerWrapper
export const PreviewContainer = StyledPreviewContainer

const segmentTrackButton = (
  button,
  index,
  type,
  location,
  artistEthAddress,
  artistName,
  genres,
  bpm,
  instrument
) => {
  window.analytics.track('Button Clicked', {
    button: button,
    token: index,
    source: window.location.pathname,
    location: location,
    type: type,
    artistName: artistName,
    artistEthAddress: artistEthAddress,
    genres: genres,
    bpm: bpm,
    instrument: instrument
  })
}

const segmentTrackArpMedia = async (button: string, type: string, index: string) => {
  const details = await ArpRegistry.getByIdAsync(index)
  let location = window.location.pathname.split('/')
  //console.log('LOCATION: +++++++++++ ' + location)
  const segmentLocation = SEGMENT_PREVIEW_LOCATIONS[location[1]] || 'unknown location'

  segmentTrackButton(
    button,
    index,
    type,
    segmentLocation,
    details.user.walletAddress,
    details.user.displayName,
    details.metadata.genres,
    details.metadata.bpm,
    getInstrumentsFromMedia(details)
  )
}

export const Player: FC<{ index: number; type: string; autoPlay: boolean }> = observer(
  ({ type, index, autoPlay = true }) => {
    if (type === MediaTypes.SONGS || type === MediaTypes.SONG)
      return <TokenSongPlayer token={index} originToken={null} autoPlay={autoPlay} />
    if (type === MediaTypes.SAMPLES || type === MediaTypes.SAMPLE)
      return <SamplePlayer token={index} originToken={null} autoPlay={autoPlay} />
    if (type === MediaTypes.STEMS || type === MediaTypes.STEM)
      return <StemPlayer token={index} originToken={null} autoPlay={autoPlay} />
    return null
  }
)

export const PreviewPlayer: FC<{
  index: number
  type: string
  blankStateTitle?: string
  autoPlay?: boolean
  css?: {}
}> = observer(({ type, index, blankStateTitle, css, autoPlay = true }) => (
  <PreviewContainerWrapper css={css}>
    <PreviewContainer>
      {index < 0 ? (
        <BlankState type={type} title={blankStateTitle} />
      ) : (
        <Player type={type} index={index} autoPlay={autoPlay} />
      )}
    </PreviewContainer>
  </PreviewContainerWrapper>
))

const OpenseaButton = styled('a', {
  all: 'unset',
  width: '35px',
  height: '35px',
  borderRadius: '$round',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  background: '#2081E2',
  color: '$gray01',
  transition: 'all 150ms ease',
  '&:hover': { opacity: 0.9 },
  '&:active': { opacity: 0.8 }
})

interface MediaPreviewProps {
  type?: string
  index: number
  hideMediaInfo?: boolean
  hideShareButton?: boolean
  hideContributors?: boolean
  blankStateTitle?: string
  autoPlay?: boolean
}
const MediaPreview: FC<MediaPreviewProps & ComponentProps<typeof PreviewBar>> = ({
  type,
  index = 0,
  hideMediaInfo,
  hideShareButton,
  blankStateTitle,
  hideContributors,
  autoPlay,
  ...props
}) => {
  const [openseaLink, setOpenseaLink] = useState<string>(null)
  const { remixSong, remixSample, remixStem, status } = useRemix()
  const navigate = useNavigate()
  const indexStr = index.toString()
  const playerLink =
    type === MediaTypes.SONGS
      ? '/song?token=' + index
      : type === MediaTypes.SAMPLES
      ? '/sample?token=' + index
      : '/stem?token=' + index

  const loadingStatus = useAsyncEffect(async () => {
    if (type === MediaTypes.SONGS) {
      const link = await getSongOpenseaLink(index)
      setOpenseaLink(link)
    }
    if (type === MediaTypes.SAMPLES) {
      const link = await getSampleOpenseaLink(index)
      setOpenseaLink(link)
    }
    if (type === MediaTypes.STEMS) {
      const link = await getStemOpenseaLink(index)
      setOpenseaLink(link)
    }
  }, [type, index])

  const handleDetails = () => {
    segmentTrackArpMedia('See Details', type, indexStr)
    navigate(playerLink)
  }
  const handleRemix = () => {
    segmentTrackArpMedia('Open in Studio', type, indexStr)
    if (type === MediaTypes.SONGS) remixSong(index)
    if (type === MediaTypes.SAMPLES) remixSample(index)
    if (type === MediaTypes.STEMS) remixStem(index)
  }

  const mType = normalizeSubtypes(type)

  return (
    <PreviewBar {...props}>
      <PreviewPlayer
        index={index}
        type={type}
        blankStateTitle={blankStateTitle}
        autoPlay={autoPlay}
      />
      {index >= 0 && (
        <Box>
          {!hideMediaInfo && <CurrentlyPlayingInfo playerType={type} index={index} />}
          {!hideContributors && <Contributors index={index} type={mType} maxContributors={6} />}
          <Box css={{ margin: '-5px', padding: '5px', paddingTop: '37px', display: 'flex' }}>
            <Button big type={'outlined'} css={{ margin: '5px', flex: 1 }} onClick={handleDetails}>
              {'See details'}
            </Button>
            <Button
              disabled={status === 'loading'}
              big
              type={'dark'}
              css={{ flex: 1, margin: '5px' }}
              onClick={handleRemix}
            >
              {status === 'ready' ? 'Open in Studio' : <Spinner />}
            </Button>
            {!hideShareButton && (
              <TweetButton
                mediaType={mType}
                index={index}
                css={{ height: '35px', width: '35px', margin: '5px' }}
              />
            )}
            {openseaLink && (
              <OpenseaButton css={{ margin: '5px' }} href={openseaLink} target="_blank">
                <OpenseaLogo style={{ width: '20px', height: '20px' }} />
              </OpenseaButton>
            )}
          </Box>
        </Box>
      )}
    </PreviewBar>
  )
}

export default observer(MediaPreview)
